import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePresupuestosProforma } from '@/presupuestos/hooks/usePresupuestosProforma';
import { formatearMonto } from '@/presupuestos/utils/formatters';

interface ModalImportarPresupuestoProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Importa el presupuesto. Debe rechazar con la respuesta del backend para poder
   * distinguir el 409 de confirmación del 409 de importación duplicada.
   */
  onConfirm: (presupuestoProformaId: string, reemplazar: boolean) => Promise<void>;
  isLoading?: boolean;
}

/**
 * Importa un presupuesto independiente confirmado dentro de una escritura.
 *
 * La importación crea una copia: los cambios posteriores en el presupuesto
 * original no modifican la escritura. Si la escritura ya tiene ítems, se pide
 * confirmación explícita antes de reemplazarlos (no hay fusión).
 */
export const ModalImportarPresupuesto: React.FC<ModalImportarPresupuestoProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const [busqueda, setBusqueda] = useState('');
  const [seleccionado, setSeleccionado] = useState<string | null>(null);
  const [confirmandoReemplazo, setConfirmandoReemplazo] = useState(false);
  const [error, setError] = useState('');

  // Solo se ofrecen los confirmados que todavía no fueron importados: un
  // presupuesto se importa en una única escritura.
  const { presupuestos, isLoading: isBuscando } = usePresupuestosProforma({
    search: busqueda || undefined,
    estado: 'confirmado',
    importado: false,
  });

  const cerrar = () => {
    setBusqueda('');
    setSeleccionado(null);
    setConfirmandoReemplazo(false);
    setError('');
    onClose();
  };

  const importar = async (reemplazar: boolean) => {
    if (!seleccionado) {
      setError('Seleccione un presupuesto para importar');
      return;
    }

    setError('');

    try {
      await onConfirm(seleccionado, reemplazar);
      cerrar();
    } catch (err: any) {
      const respuesta = err?.response?.data;

      if (err?.response?.status === 409 && respuesta?.requiereConfirmacion) {
        setConfirmandoReemplazo(true);
        return;
      }

      setError(respuesta?.message ?? 'No se pudo importar el presupuesto.');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(abierto) => !abierto && !isLoading && cerrar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Importar presupuesto</DialogTitle>
        </DialogHeader>

        {confirmandoReemplazo ? (
          <div className="space-y-3 py-4">
            <div className="flex items-start gap-2 rounded-md border border-destructive/20 bg-destructive/10 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 text-destructive" />
              <div className="text-sm">
                <p className="font-medium text-destructive">
                  Esta escritura ya tiene ítems de presupuesto
                </p>
                <p className="mt-1 text-muted-foreground">
                  Al importar se reemplazan todos los ítems existentes. Esta acción no
                  se puede deshacer.
                </p>
              </div>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        ) : (
          <div className="space-y-3 py-4">
            <div className="space-y-1.5">
              <Label htmlFor="buscar-presupuesto">Buscar presupuesto confirmado</Label>
              <Input
                id="buscar-presupuesto"
                value={busqueda}
                onChange={(event) => setBusqueda(event.target.value)}
                placeholder="Nombre del presupuesto"
                disabled={isLoading}
              />
            </div>

            <div className="max-h-64 space-y-2 overflow-y-auto">
              {isBuscando ? (
                <p className="py-6 text-center text-sm text-muted-foreground">Buscando…</p>
              ) : presupuestos.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No hay presupuestos confirmados que coincidan.
                </p>
              ) : (
                presupuestos.map((presupuesto) => (
                  <button
                    key={presupuesto.id}
                    type="button"
                    onClick={() => {
                      setSeleccionado(presupuesto.id);
                      setError('');
                    }}
                    className={`w-full rounded-lg border p-3 text-left transition-colors hover:bg-accent/50 ${
                      seleccionado === presupuesto.id ? 'border-primary bg-accent/40' : ''
                    }`}
                  >
                    <div className="font-medium">{presupuesto.nombre}</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Adquiriente {formatearMonto(presupuesto.totales.adquiriente.ARS)} ·
                      Transferente {formatearMonto(presupuesto.totales.transferente.ARS)}
                    </div>
                  </button>
                ))
              )}
            </div>

            <p className="text-xs text-muted-foreground">
              Se copian los ítems con su descripción, monto, moneda y parte. El
              presupuesto original queda independiente de esta escritura.
            </p>

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={cerrar} disabled={isLoading}>
            Cancelar
          </Button>
          {confirmandoReemplazo ? (
            <Button
              variant="destructive"
              onClick={() => void importar(true)}
              disabled={isLoading}
            >
              Reemplazar todos los ítems
            </Button>
          ) : (
            <Button
              onClick={() => void importar(false)}
              disabled={isLoading || !seleccionado}
            >
              Importar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
